import { useFormikContext } from "formik";
import { useMemo } from "react";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import type { TaskType } from "@common/types/task";
import { getBlockedTaskTypesForPlan } from "@common/constants/taskTypePlanRequirements";
import type { ApplyCloseTemplateEntitySelectionRow } from "./applyCloseTemplateEntitySelection.types";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import {
    applyCloseTemplateEntitySelectionViewConfig,
    ApplyCloseTemplateEntityColumn,
} from "./applyCloseTemplateEntitySelectionViewConfig";

import { useApplyCloseTemplateEntityFilters } from "./useApplyCloseTemplateEntityFilters";
import { useApplyCloseTemplateEntityBulkSelection } from "./useApplyCloseTemplateEntityBulkSelection";
import { ApplyCloseTemplateEntityFilterBar } from "./ApplyCloseTemplateEntityFilterBar";
import { ApplyCloseTemplateEntitySelectionFooter } from "./ApplyCloseTemplateEntitySelectionFooter";
import styles from "./ApplyCloseTemplateDrawer.module.scss";

const ROLE_COLUMNS: Array<{
    column: ApplyCloseTemplateEntityColumn;
    role: TaskTemplateAssigneeRole;
}> = [
    {
        column: ApplyCloseTemplateEntityColumn.PREPARER,
        role: TaskTemplateAssigneeRole.PREPARER,
    },
    {
        column: ApplyCloseTemplateEntityColumn.REVIEWER,
        role: TaskTemplateAssigneeRole.REVIEWER,
    },
    {
        column: ApplyCloseTemplateEntityColumn.MANAGER,
        role: TaskTemplateAssigneeRole.MANAGER,
    },
];

interface Props {
    workspaces: WorkspaceSchema[] | undefined;
    activeRoles: Set<TaskTemplateAssigneeRole>;
    templateTaskTypes: TaskType[];
    onBack: () => void;
    onContinue: () => void;
    subtitle: string;
    searchPlaceholder: string;
    /** Whether to render the client-level Plans filter (hidden for single-client templates). */
    showPlansFilter: boolean;
    /** The schedule variant derives dates from the recurrence's occurrences rather than per-entity, so the start/end date columns don't apply. */
    shouldHideDateColumns?: boolean;
    /** Entity UUIDs that already have a schedule for this template (schedule variant only). */
    scheduledEntityUuids?: Set<string>;
}

export const ApplyCloseTemplateStepEntitySelection = ({
    workspaces,
    activeRoles,
    templateTaskTypes,
    onBack,
    onContinue,
    subtitle,
    searchPlaceholder,
    showPlansFilter,
    shouldHideDateColumns,
    scheduledEntityUuids,
}: Props) => {
    const formik = useFormikContext<ApplyCloseTemplateWizardValues>();

    const {
        search,
        setSearch,
        selectedPlans,
        setSelectedPlans,
        selectedIndustries,
        setSelectedIndustries,
        selectedLegalTypes,
        setSelectedLegalTypes,
        view,
        setView,
        filteredTableData: baseFilteredTableData,
    } = useApplyCloseTemplateEntityFilters(workspaces);

    const filteredViewConfig = useMemo(() => {
        const excludedColumns = new Set(
            ROLE_COLUMNS.filter(({ role }) => !activeRoles.has(role)).map(
                ({ column }) => column,
            ),
        );
        if (shouldHideDateColumns) {
            excludedColumns.add(ApplyCloseTemplateEntityColumn.START_DATE);
            excludedColumns.add(ApplyCloseTemplateEntityColumn.END_DATE);
            excludedColumns.add(ApplyCloseTemplateEntityColumn.WORK_START);
            excludedColumns.add(ApplyCloseTemplateEntityColumn.WORK_END);
        }
        if (excludedColumns.size === 0) {
            return applyCloseTemplateEntitySelectionViewConfig;
        }
        const columns = {
            ...applyCloseTemplateEntitySelectionViewConfig.columns,
        };
        for (const col of excludedColumns) {
            delete columns[col];
        }
        return {
            ...applyCloseTemplateEntitySelectionViewConfig,
            columns,
        };
    }, [activeRoles, shouldHideDateColumns]);

    // Enrich the filtered rows from the hook with plan-blocking and already-
    // scheduled info. The hook handles search/plan/industry filtering; this
    // layer adds flags so the table can disable blocked entities and the
    // bulk-selection logic can exclude them.
    const filteredTableData = useMemo<ApplyCloseTemplateEntitySelectionRow[]>(
        () =>
            baseFilteredTableData.map((wsRow) => {
                const blockedTaskTypes = getBlockedTaskTypesForPlan(
                    templateTaskTypes,
                    wsRow.plan ?? null,
                );
                const isPlanBlocked = blockedTaskTypes.length > 0;
                const children = wsRow.children?.map((child) => {
                    const isAlreadyScheduled =
                        child.entityUuid != null &&
                        (scheduledEntityUuids?.has(child.entityUuid) ?? false);
                    const isSelectionDisabled =
                        isPlanBlocked || isAlreadyScheduled;
                    return {
                        ...child,
                        blockedTaskTypes,
                        isPlanBlocked,
                        isAlreadyScheduled,
                        isSelectionDisabled,
                    };
                });
                const isSelectionDisabled =
                    isPlanBlocked ||
                    (children != null &&
                        children.length > 0 &&
                        children.every((c) => c.isSelectionDisabled));
                return {
                    ...wsRow,
                    blockedTaskTypes,
                    isPlanBlocked,
                    isSelectionDisabled,
                    children,
                };
            }),
        [baseFilteredTableData, scheduledEntityUuids, templateTaskTypes],
    );

    // Exclude blocked entities (plan or already scheduled) from the selectable set.
    const filteredEntityIds = useMemo(
        () =>
            new Set(
                filteredTableData.flatMap(
                    (wsRow) =>
                        wsRow.children
                            ?.filter((c) => !c.isSelectionDisabled)
                            .map((c) => c.id) ?? [],
                ),
            ),
        [filteredTableData],
    );

    const tableDataWithSelection = useMemo(
        () =>
            filteredTableData.map((wsRow) => {
                const children = wsRow.children?.map((child) => ({
                    ...child,
                    isApplicationSelected:
                        !child.isSelectionDisabled &&
                        (formik.values.entityRows[child.id]?.isSelected ??
                            false),
                }));
                const selectableChildren = children?.filter(
                    (c) => !c.isSelectionDisabled,
                );
                return {
                    ...wsRow,
                    isApplicationSelected:
                        (selectableChildren?.length ?? 0) > 0 &&
                        (selectableChildren?.every(
                            (c) => c.isApplicationSelected,
                        ) ??
                            false),
                    children,
                };
            }),
        [filteredTableData, formik.values.entityRows],
    );

    const {
        bulkRowSelection,
        onRowSelectionChange,
        tableMeta,
        selectedBulkIds,
        totalFilteredEntityCount,
        hasSelectedEntities,
        hasAllSelectedBulkEntitiesSelected,
        handleSelectAll,
        handleUnselectSelected,
        handleOpenBulkActions,
        handleOpenBulkPreparer,
        handleOpenBulkReviewer,
        handleOpenBulkManager,
        handleSelectForApplication,
        handleDeselectForApplication,
    } = useApplyCloseTemplateEntityBulkSelection({
        filteredEntityIds,
        activeRoles,
    });

    if (!workspaces?.length) {
        return <p className="text-muted small">No clients available yet.</p>;
    }

    return (
        <>
            <div className={styles.subtitleContainer}>{subtitle}</div>
            <ApplyCloseTemplateEntityFilterBar
                search={search}
                onSearchChange={setSearch}
                selectedPlans={selectedPlans}
                onPlansChange={setSelectedPlans}
                selectedIndustries={selectedIndustries}
                onIndustriesChange={setSelectedIndustries}
                selectedLegalTypes={selectedLegalTypes}
                onLegalTypesChange={setSelectedLegalTypes}
                view={view}
                onViewChange={setView}
                viewConfig={filteredViewConfig}
                searchPlaceholder={searchPlaceholder}
                showPlansFilter={showPlansFilter}
            />
            <div className={styles.entitySelectionTableWrap}>
                <ViewTable
                    data={tableDataWithSelection}
                    view={view}
                    config={filteredViewConfig}
                    onViewChange={setView}
                    rowSelection={bulkRowSelection}
                    onRowSelectionChange={onRowSelectionChange}
                    tableMeta={tableMeta}
                    enableColumnResizing={false}
                    autoExpandOnMount
                    fixedTopOffsetPx
                    emptyView={() => (
                        <EmptyState header="No entities in this organization" />
                    )}
                    footer={
                        <ApplyCloseTemplateEntitySelectionFooter
                            selectedBulkIds={selectedBulkIds}
                            totalFilteredEntityCount={totalFilteredEntityCount}
                            activeRoles={activeRoles}
                            hasSelectedEntities={hasSelectedEntities}
                            hasAllSelectedBulkEntitiesSelected={
                                hasAllSelectedBulkEntitiesSelected
                            }
                            onSelectAll={handleSelectAll}
                            onUnselectSelected={handleUnselectSelected}
                            onSelectForApplication={handleSelectForApplication}
                            onDeselectForApplication={
                                handleDeselectForApplication
                            }
                            onOpenBulkPreparer={handleOpenBulkPreparer}
                            onOpenBulkReviewer={handleOpenBulkReviewer}
                            onOpenBulkManager={handleOpenBulkManager}
                            onOpenBulkActions={handleOpenBulkActions}
                            onBack={onBack}
                            onContinue={onContinue}
                        />
                    }
                />
            </div>
        </>
    );
};
